import * as React from "react";
import { motion, useAnimationFrame, useMotionValue } from "motion/react";
import { cn } from "~/lib/cn";

export type CarouselImage = {
  src: string;
  alt?: string;
  href?: string;
};

type Direction = "left" | "right";

export type DoubleImageCarouselProps = {
  images: CarouselImage[];
  className?: string;
  rowHeight?: number; // in px
  gapPx?: number; // space between cards
  speedPxPerSecond?: number; // pixels per second for constant speed
  topDirection?: Direction;
  bottomDirection?: Direction;
  pauseOnHover?: boolean;
  maskEdges?: boolean;
  maskBackgroundColor?: string; // CSS color for mask edges
  rounded?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full";
  imageFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  showSecondRow?: boolean; // enable/disable second row
  randomizeOrder?: boolean; // randomize image order for each row
  hoverScale?: number; // scale factor on hover
};

const roundedClassMap: Record<
  NonNullable<DoubleImageCarouselProps["rounded"]>,
  string
> = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  "3xl": "rounded-3xl",
  full: "rounded-full",
};

const DEFAULTS: Required<
  Pick<
    DoubleImageCarouselProps,
    | "rowHeight"
    | "gapPx"
    | "speedPxPerSecond"
    | "topDirection"
    | "bottomDirection"
    | "pauseOnHover"
    | "maskEdges"
    | "maskBackgroundColor"
    | "rounded"
    | "imageFit"
    | "showSecondRow"
    | "randomizeOrder"
    | "hoverScale"
  >
> = {
  rowHeight: 260,
  gapPx: 24,
  speedPxPerSecond: 30,
  topDirection: "left",
  bottomDirection: "right",
  pauseOnHover: true,
  maskEdges: true,
  maskBackgroundColor: "hsl(var(--background))",
  rounded: "xl",
  imageFit: "cover",
  showSecondRow: true,
  randomizeOrder: true,
  hoverScale: 1.04,
};

// Strongly-typed CSS variables for CSS custom props
type CarouselCssVars = React.CSSProperties & {
  "--carousel-gap"?: string;
  "--carousel-height"?: string;
  "--row-height"?: string;
};

// Helper function to shuffle array
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function DoubleImageCarousel(props: DoubleImageCarouselProps) {
  const settings = { ...DEFAULTS, ...props };

  // Guard: need at least one image
  if (!settings.images || settings.images.length === 0) {
    return null;
  }

  const roundedClass = roundedClassMap[settings.rounded];

  // Prepare images for each row with optional randomization (memoized to avoid re-shuffle)
  const topImages = React.useMemo(
    () =>
      settings.randomizeOrder ? shuffleArray(settings.images) : settings.images,
    [settings.images, settings.randomizeOrder],
  );
  const bottomImages = React.useMemo(
    () =>
      settings.randomizeOrder ? shuffleArray(settings.images) : settings.images,
    [settings.images, settings.randomizeOrder],
  );

  // Compute vertical padding to keep hover scale inside section bounds
  const verticalPadPx = Math.max(
    0,
    Math.ceil(((settings.hoverScale - 1) * settings.rowHeight) / 2) + 4,
  );

  return (
    <section
      className={cn(
        "relative z-0 w-full select-none overflow-x-hidden overflow-y-visible has-[.carousel-hoverable:hover]:z-20",
        settings.className,
      )}
      style={
        {
          "--carousel-gap": `${settings.gapPx}px`,
          "--carousel-height": `${settings.rowHeight}px`,
          "--hover-scale": settings.hoverScale.toString(),
          paddingTop: `${verticalPadPx}px`,
          paddingBottom: `${verticalPadPx}px`,
        } as CarouselCssVars
      }
    >
      {settings.maskEdges ? (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24"
            style={{
              background: `linear-gradient(to right, ${settings.maskBackgroundColor}, transparent)`,
            }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24"
            style={{
              background: `linear-gradient(to left, ${settings.maskBackgroundColor}, transparent)`,
            }}
          />
        </>
      ) : null}

      <div className="flex flex-col gap-6">
        <MarqueeRow
          images={topImages}
          direction={settings.topDirection}
          speedPxPerSecond={settings.speedPxPerSecond}
          gapPx={settings.gapPx}
          pauseOnHover={settings.pauseOnHover}
          roundedClass={roundedClass}
          imageFit={settings.imageFit}
        />
        {settings.showSecondRow && (
          <MarqueeRow
            images={bottomImages}
            direction={settings.bottomDirection}
            speedPxPerSecond={settings.speedPxPerSecond * 1.12}
            gapPx={settings.gapPx}
            pauseOnHover={settings.pauseOnHover}
            roundedClass={roundedClass}
            imageFit={settings.imageFit}
          />
        )}
      </div>
    </section>
  );
}

type MarqueeRowProps = {
  images: CarouselImage[];
  direction: Direction;
  speedPxPerSecond: number;
  gapPx: number;
  pauseOnHover: boolean;
  roundedClass: string;
  imageFit: NonNullable<DoubleImageCarouselProps["imageFit"]>;
};

function MarqueeRow({
  images,
  direction,
  speedPxPerSecond,
  gapPx: _gapPx,
  pauseOnHover,
  roundedClass,
  imageFit,
}: MarqueeRowProps) {
  // Build duplicated content in a single track so gaps apply across the seam
  const duplicated = React.useMemo(() => [...images, ...images], [images]);

  const trackRef = React.useRef<HTMLDivElement>(null);
  const [halfWidth, setHalfWidth] = React.useState(0);
  const x = useMotionValue(0);
  const [isHovered, setIsHovered] = React.useState(false);
  const [prefersReduced, setPrefersReduced] = React.useState(false);

  // Measure half of the track width (one set of images)
  const measure = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const total = el.scrollWidth;
    const half = total / 2;
    setHalfWidth(half);
    // Initialize position so rightward motion doesn't reveal empty space
    if (direction === "right") {
      x.set(-half);
    } else {
      x.set(0);
    }
  }, [direction, x]);

  React.useEffect(() => {
    measure();
    const el = trackRef.current;
    if (!el) return;
    if (typeof window !== "undefined" && "ResizeObserver" in window) {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, [images, measure]);

  // Re-measure when any image finishes loading
  const handleImgLoad = React.useCallback(() => measure(), [measure]);

  // Respect reduced motion user preference
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReduced(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  // Drive continuous motion frame-by-frame for perfect seamless loop
  useAnimationFrame((_, delta) => {
    if (isHovered || prefersReduced || halfWidth === 0) return;
    const distancePerMs = speedPxPerSecond / 1000;
    const deltaPx = distancePerMs * delta;
    const current = x.get();
    if (direction === "left") {
      let next = current - deltaPx;
      if (next <= -halfWidth) next += halfWidth;
      x.set(next);
    } else {
      let next = current + deltaPx;
      if (next >= halfWidth) next -= halfWidth;
      x.set(next);
    }
  });

  return (
    <div
      className={cn("relative overflow-visible")}
      style={{ "--row-height": `var(--carousel-height)` } as CarouselCssVars}
    >
      <div className="overflow-visible">
        <motion.div
          className={cn("flex items-center will-change-transform")}
          style={{ x }}
          onMouseEnter={pauseOnHover ? () => setIsHovered(true) : undefined}
          onMouseLeave={pauseOnHover ? () => setIsHovered(false) : undefined}
        >
          <div
            ref={trackRef}
            className="flex min-w-max items-center gap-[var(--carousel-gap)]"
          >
            {duplicated.map((img, index) => (
              <ImageCard
                key={`${img.src}-${index}`}
                image={img}
                roundedClass={roundedClass}
                imageFit={imageFit}
                onLoad={index < images.length ? handleImgLoad : undefined}
                isDuplicate={index >= images.length}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function ImageCard({
  image,
  roundedClass,
  imageFit,
  onLoad,
  isDuplicate = false,
}: {
  image: CarouselImage;
  roundedClass: string;
  imageFit: NonNullable<DoubleImageCarouselProps["imageFit"]>;
  onLoad?: () => void;
  isDuplicate?: boolean;
}) {
  const img = (
    <img
      src={image.src}
      alt={image.alt ?? ""}
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
      className={cn(
        "h-[var(--carousel-height)] w-auto shrink-0 transition-transform duration-300 ease-out will-change-transform",
        imageFit === "cover" ? "object-cover" : undefined,
        imageFit === "contain" ? "object-contain" : undefined,
        imageFit === "fill" ? "object-fill" : undefined,
        imageFit === "none" ? "object-none" : undefined,
        imageFit === "scale-down" ? "object-scale-down" : undefined,
        roundedClass,
      )}
      style={{ aspectRatio: "16 / 9" }}
    />
  );

  const content = (
    <div
      className={cn(
        "group relative z-0 shrink-0 transition-transform duration-300 ease-out will-change-transform",
      )}
      aria-hidden={isDuplicate ? true : undefined}
    >
      <div
        className={cn(
          "relative bg-background shadow-sm overflow-visible",
          "border-black/10 dark:border-white/10",
          roundedClass,
        )}
      >
        <div
          className={cn("pointer-events-none absolute inset-0", roundedClass)}
        />
        {img}
      </div>
    </div>
  );

  if (image.href) {
    return (
      <a
        href={image.href}
        className="block"
        aria-label={image.alt ?? undefined}
        tabIndex={isDuplicate ? -1 : undefined}
        aria-hidden={isDuplicate ? true : undefined}
      >
        <div className="relative z-0 -mx-[2px] -my-[2px] px-[2px] py-[2px]">
          <div className="carousel-hoverable relative z-0 transition-transform duration-300 ease-out hover:z-10 hover:[transform:scale(var(--hover-scale))] hover:drop-shadow-xl">
            {content}
          </div>
        </div>
      </a>
    );
  }

  return (
    <div className="relative z-0 -mx-[2px] -my-[2px] px-[2px] py-[2px]">
      <div className="carousel-hoverable relative z-0 transition-transform duration-300 ease-out hover:z-10 hover:[transform:scale(var(--hover-scale))] hover:drop-shadow-xl">
        {content}
      </div>
    </div>
  );
}

export default DoubleImageCarousel;
